import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Spin, message } from "antd";
import { ArrowRightOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Amount } from "fin-web-components";
import { StoreOrderApi } from "../../order/StoreOrderApi";
import type { GetMembershipUpgradePreviewResult } from "../../order/StoreOrderType";
import {
  isDuplicateUnpaidCreateResult,
  promptDuplicateUnpaidOrder,
} from "../../../customer_account/storeOrderDuplicateUnpaidPrompt";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import "../SpaceStyle.css";

export type SpaceProductUpgradeModalProps = {
  spaceId: string;
  row: MembershipProduct;
  open: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  spaceProfile: SpaceProfile | null;
  checkoutPath?: (orderId: string) => string;
  orderDetailPath?: (orderId: string) => string;
};

function calcPayYuan(priceYuan: number, creditYuan: number): number {
  const pay = Math.max(0.01, priceYuan - creditYuan);
  return Math.round(pay * 100) / 100;
}

export function SpaceProductUpgradeModal(props: SpaceProductUpgradeModalProps): JSX.Element {
  const { spaceId, row, open, onClose, isLoggedIn, onLoginRequest, checkoutPath, orderDetailPath } =
    props;
  const navigate = useNavigate();
  const sourceId = row.upgradeSourceSpaceProductId?.trim() ?? "";
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<GetMembershipUpgradePreviewResult | null>(null);
  const [tierId, setTierId] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await StoreOrderApi.getMembershipUpgradePreview({
        spaceId,
        spaceProductId: row.spaceProductId,
        upgradeFromSpaceProductId: sourceId || undefined,
      });
      if (typeof res === "string") {
        message.error(res);
        setPreview(null);
        return;
      }
      if (!res) {
        message.error("暂无法升级，请稍后重试");
        setPreview(null);
        return;
      }
      setPreview(res);
      const specs = res.eligiblePriceSpecs ?? [];
      setTierId(specs[0]?.tierId ?? null);
    } finally {
      setLoading(false);
    }
  }, [row.spaceProductId, sourceId, spaceId]);

  useEffect(() => {
    if (!open || !isLoggedIn) {
      return;
    }
    void loadPreview();
  }, [open, isLoggedIn, loadPreview]);

  const specs: GetMembershipUpgradePreviewResult["eligiblePriceSpecs"] =
    preview?.eligiblePriceSpecs ?? [];
  const selectedSpec = specs.find((s) => s.tierId === tierId) ?? specs[0] ?? null;
  const credit = preview?.upgradeCreditYuan ?? 0;
  const listPrice = selectedSpec?.priceYuan ?? 0;
  const payYuan = useMemo(
    () => (selectedSpec ? calcPayYuan(listPrice, credit) : 0),
    [credit, listPrice, selectedSpec]
  );

  const currentNames: string[] = preview?.currentBundledDisplayNames ?? [];
  const targetNames: string[] = preview?.targetBundledDisplayNames ?? [];
  const addedNames = targetNames.filter((n) => !currentNames.includes(n));
  const sourceOwnedLabel = preview?.sourceIsProductBundle === true ? "已购套餐" : "已购产品";

  const confirmUpgrade = async () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (!tierId || !selectedSpec) {
      message.warning("请选择升级规格");
      return;
    }
    setSubmitting(true);
    try {
      const createOnce = (forceCreate: boolean) =>
        StoreOrderApi.createOrder({
          spaceId,
          spaceProductId: row.spaceProductId,
          serviceTier: tierId,
          upgradeFromSpaceProductId: sourceId || undefined,
          forceCreate: forceCreate || undefined,
        });

      let res = await createOnce(false);
      if (typeof res === "string") {
        message.error(res);
        return;
      }
      if (isDuplicateUnpaidCreateResult(res)) {
        const action = await promptDuplicateUnpaidOrder(res.failMessage ?? undefined);
        if (action === "pay") {
          onClose();
          const detailPath = orderDetailPath
            ? orderDetailPath(res.existingOrderId)
            : `/orders/${spaceId}/${encodeURIComponent(res.existingOrderId)}`;
          navigate(detailPath);
          return;
        }
        res = await createOnce(true);
        if (typeof res === "string") {
          message.error(res);
          return;
        }
      }
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      onClose();
      const path = checkoutPath
        ? checkoutPath(res.orderId)
        : `/checkout/${spaceId}/${encodeURIComponent(res.orderId)}`;
      navigate(path);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={null}
      footer={null}
      closable={false}
      open={open}
      onCancel={onClose}
      width={560}
      centered
      destroyOnClose
      maskClosable
      className="space-upgrade-modal"
      wrapClassName="space-upgrade-modal-wrap"
    >
      <button type="button" className="space-upgrade-modal__close" aria-label="关闭" onClick={onClose}>
        <CloseOutlined />
      </button>

      <header className="space-upgrade-modal__head">
        <h2 className="space-upgrade-modal__title">升级至「{row.displayName}」</h2>
        <p className="space-upgrade-modal__lead">
          支付成功后，原套餐「{preview?.sourceProductName ?? row.upgradeSourceDisplayName ?? "当前套餐"}」权益立即结束，新套餐权益即刻生效。
        </p>
      </header>

      <Spin spinning={loading}>
        {preview ? (
          <div className="space-upgrade-modal__body">
            <div className="space-upgrade-modal__compare">
              <section className="space-upgrade-modal__panel space-upgrade-modal__panel--from">
                <p className="space-upgrade-modal__panel-label">{sourceOwnedLabel}</p>
                <p className="space-upgrade-modal__panel-name">{preview.sourceProductName}</p>
                <p className="space-upgrade-modal__panel-meta">
                  剩余 {preview.sourceDaysRemaining ?? 0} 天 · 抵扣约{" "}
                  <Amount value={preview.upgradeCreditYuan} fontSize={13} fontWeight={400} />
                </p>
                <ul className="space-upgrade-modal__service-list">
                  {currentNames.map((name) => (
                    <li key={name}>
                      <CheckOutlined className="space-upgrade-modal__service-icon" />
                      <span>{name}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="space-upgrade-modal__arrow" aria-hidden>
                <ArrowRightOutlined />
              </div>

              <section className="space-upgrade-modal__panel space-upgrade-modal__panel--to">
                <p className="space-upgrade-modal__panel-label">升级后</p>
                <p className="space-upgrade-modal__panel-name">{preview.targetProductName}</p>
                {addedNames.length > 0 ? (
                  <p className="space-upgrade-modal__panel-meta space-upgrade-modal__panel-meta--new">
                    新增 {addedNames.length} 项服务
                  </p>
                ) : (
                  <p className="space-upgrade-modal__panel-meta">服务范围扩展</p>
                )}
                <ul className="space-upgrade-modal__service-list">
                  {targetNames.map((name) => {
                    const isNew = addedNames.includes(name);
                    return (
                      <li key={name} className={isNew ? "space-upgrade-modal__service-item--new" : undefined}>
                        <CheckOutlined className="space-upgrade-modal__service-icon" />
                        <span>{name}</span>
                        {isNew ? <span className="space-upgrade-modal__tag-new">新增</span> : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            {specs.length > 0 ? (
              <section className="space-upgrade-modal__tiers" aria-label="选择升级规格">
                <p className="space-upgrade-modal__section-label">选择规格</p>
                <div className="space-upgrade-modal__tier-grid">
                  {specs.map((s) => {
                    const active = tierId === s.tierId;
                    return (
                      <button
                        key={s.tierId}
                        type="button"
                        className={
                          active
                            ? "space-upgrade-tier-card space-upgrade-tier-card--active"
                            : "space-upgrade-tier-card"
                        }
                        onClick={() => setTierId(s.tierId)}
                      >
                        <span className="space-upgrade-tier-card__name">
                          {s.specName || s.durationLabel}
                        </span>
                        <span className="space-upgrade-tier-card__price">¥{s.priceYuan}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {selectedSpec ? (
              <section className="space-upgrade-modal__settlement" aria-label="费用明细">
                <div className="space-upgrade-modal__settlement-row">
                  <span>规格标价</span>
                  <Amount value={listPrice} fontSize={14} fontWeight={400} />
                </div>
                <div className="space-upgrade-modal__settlement-row space-upgrade-modal__settlement-row--credit">
                  <span>原权益抵扣</span>
                  <span>
                    −<Amount value={credit} fontSize={14} fontWeight={400} />
                  </span>
                </div>
                <div className="space-upgrade-modal__settlement-row space-upgrade-modal__settlement-row--pay">
                  <span>应付金额</span>
                  <Amount value={payYuan} fontSize={18} fontWeight={400} />
                </div>
                <p className="space-upgrade-modal__deadline-hint">订单创建后 30 分钟内有效，请及时支付</p>
              </section>
            ) : null}
          </div>
        ) : !loading ? (
          <p className="space-upgrade-modal__empty">暂无升级信息</p>
        ) : null}
      </Spin>

      <footer className="space-upgrade-modal__footer">
        <Button className="space-upgrade-modal__footer-btn" style={{ minWidth: 120 }} onClick={onClose}>
          取消
        </Button>
        <Button
          type="primary"
          className="space-upgrade-modal__footer-btn space-upgrade-modal__footer-btn--primary"
          style={{ minWidth: 120 }}
          loading={submitting}
          disabled={!preview || !selectedSpec}
          onClick={() => void confirmUpgrade()}
        >
          确认升级
        </Button>
      </footer>
    </Modal>
  );
}
